import React, { useMemo, useState, useEffect } from "react";
import { Search, Plus, ExternalLink, Home as HomeIcon, CheckCircle2, PlayCircle, Upload } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { KWLogo } from "@/components/KWLogo";
import { ListingCard } from "@/components/ListingCard";
import { PostListingModal } from "@/components/PostListingModal";
import { ClaimModal } from "@/components/ClaimModal";
import { ReportActivityModal } from "@/components/ReportActivityModal";
import { VideoTutorialModal } from "@/components/VideoTutorialModal";
import { CsvImportModal } from "@/components/CsvImportModal";
import { HUB, REPORT_MODAL, CSV_MODAL } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOOGLE_DRIVE_URL = "https://docs.google.com/forms/d/e/1FAIpQLSdNn30VVC5rYd3Ph2WFgZJMuFO591yTKyI_wQVW2XrC-k6XoA/viewform?usp=publish-editor";
// Kept as a reference for the checklist copy inside ClaimModal; the header button now opens an in-app report form.
const _GOOGLE_DRIVE_URL = GOOGLE_DRIVE_URL;
void _GOOGLE_DRIVE_URL;

const TUTORIALS = {
  post: {
    title: "How to Post an Opportunity",
    src: "https://kommodo.ai/embed/recordings/mUnrdCTrLA9ysf4EHIYH?onlyRecording=1",
  },
  claim: {
    title: "How to Claim an Opportunity",
    src: "https://kommodo.ai/embed/recordings/is17JfSSNNicwyApeSZw?onlyRecording=1",
  },
};

export const OpenHouseHub = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("available");
  const [query, setQuery] = useState("");
  const [postOpen, setPostOpen] = useState(false);
  const [claimTarget, setClaimTarget] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [tutorial, setTutorial] = useState(null); // "post" | "claim" | null
  const [csvOpen, setCsvOpen] = useState(false);
  const [postPrefill, setPostPrefill] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(`${API}/listings`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        setListings(data.listings || []);
      } catch (err) {
        console.error("Failed to load listings:", err?.message || err);
        toast.error("Couldn't load listings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const availableCount = useMemo(
    () => listings.filter((l) => l.status === "available").length,
    [listings]
  );
  const claimedCount = useMemo(
    () => listings.filter((l) => l.status === "claimed").length,
    [listings]
  );

  const visibleListings = useMemo(() => {
    const q = query.trim().toLowerCase();
    return listings
      .filter((l) => (tab === "available" ? l.status === "available" : l.status === "claimed"))
      .filter((l) => {
        if (!q) return true;
        return (
          l.address.toLowerCase().includes(q) ||
          l.city.toLowerCase().includes(q) ||
          l.county.toLowerCase().includes(q) ||
          l.agentName.toLowerCase().includes(q) ||
          (l.claimedBy &&
            (`${l.claimedBy.firstName} ${l.claimedBy.lastName}`.toLowerCase().includes(q)))
        );
      })
      .sort((a, b) => new Date(a.availableDate) - new Date(b.availableDate));
  }, [listings, tab, query]);

  const handlePost = async (payload) => {
    const resp = await fetch(`${API}/listings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const doc = await resp.json();
    setListings((prev) => [doc, ...prev]);
    return doc;
  };

  const handleConfirmClaim = async (listingId, claimedBy) => {
    const resp = await fetch(`${API}/listings/${listingId}/claim`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(claimedBy),
    });
    if (!resp.ok) {
      const detail = await resp.json().catch(() => ({}));
      const err = new Error(detail.detail || `HTTP ${resp.status}`);
      err.status = resp.status;
      throw err;
    }
    const updated = await resp.json();
    setListings((prev) => prev.map((l) => (l.id === listingId ? updated : l)));
    setClaimTarget(null);
    return updated;
  };

  return (
    <div className="min-h-screen bg-[color:var(--kw-cream)]">
      <Toaster position="top-right" richColors closeButton />

      {/* KW red top bar */}
      <div className="kw-red-bar h-1.5 w-full" />

      {/* Header */}
      <header
        data-testid={HUB.header}
        className="bg-white border-b border-[color:var(--kw-border)]"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-5 md:py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4" data-testid={HUB.logo}>
            <KWLogo className="h-11 md:h-14" />
            <div className="hidden md:block h-9 w-px bg-[color:var(--kw-border)]" />
            <h1 className="hidden md:block font-display text-xl font-black uppercase tracking-tight text-[color:var(--kw-charcoal)] leading-none">
              Open House Hub
            </h1>
          </div>

          <button
            data-testid={REPORT_MODAL.openBtn}
            onClick={() => setReportOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 border-[color:var(--kw-charcoal)] text-[color:var(--kw-charcoal)] font-bold text-xs tracking-wider uppercase hover:bg-[color:var(--kw-charcoal)] hover:text-white transition-colors"
          >
            <ExternalLink size={14} strokeWidth={2.5} />
            ASC Agents&rsquo; Self-Report Held Open Houses
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="hero-accent border-b border-[color:var(--kw-border)]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[color:var(--kw-border)] mb-4">
              <span className="w-2 h-2 rounded-full bg-[color:var(--kw-teal)] animate-pulse" />
              <span className="label-caps text-[color:var(--kw-teal-dark)]">
                {availableCount} Open Opportunit{availableCount === 1 ? "y" : "ies"} Available
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase text-[color:var(--kw-charcoal)] leading-[0.95] tracking-tight">
              Wellington <span className="text-[color:var(--kw-red)]">Open House</span> Hub
            </h2>
            <p className="mt-3 text-base md:text-lg text-[color:var(--kw-charcoal-soft)] max-w-2xl leading-relaxed">
              Post listings that need hosting, or claim an open house opportunity from a fellow KW Wellington agent — all in one place.
            </p>

            {/* Tutorial buttons */}
            <div className="mt-5 flex flex-wrap gap-2.5">
              <button
                data-testid="tutorial-post-btn"
                onClick={() => setTutorial("post")}
                className="group inline-flex items-center gap-2 pl-2 pr-4 py-1.5 rounded-full bg-white border border-[color:var(--kw-border)] hover:border-[color:var(--kw-red)] shadow-sm text-xs font-bold tracking-widest uppercase text-[color:var(--kw-charcoal)] transition-all hover:-translate-y-0.5"
              >
                <span className="w-7 h-7 rounded-full bg-[color:var(--kw-red)] text-white flex items-center justify-center group-hover:scale-105 transition-transform">
                  <PlayCircle size={16} strokeWidth={2.5} />
                </span>
                <span>Watch · How to Post an Opportunity</span>
              </button>
              <button
                data-testid="tutorial-claim-btn"
                onClick={() => setTutorial("claim")}
                className="group inline-flex items-center gap-2 pl-2 pr-4 py-1.5 rounded-full bg-white border border-[color:var(--kw-border)] hover:border-[color:var(--kw-teal)] shadow-sm text-xs font-bold tracking-widest uppercase text-[color:var(--kw-charcoal)] transition-all hover:-translate-y-0.5"
              >
                <span className="w-7 h-7 rounded-full bg-[color:var(--kw-teal)] text-white flex items-center justify-center group-hover:scale-105 transition-transform">
                  <PlayCircle size={16} strokeWidth={2.5} />
                </span>
                <span>Watch · How to Claim an Opportunity</span>
              </button>
            </div>
          </div>

          {/* Action bar */}
          <div className="mt-8 flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
            <div className="relative flex-1">
              <Search
                size={18}
                strokeWidth={2.2}
                className="absolute left-5 top-1/2 -translate-y-1/2 text-[color:var(--muted-foreground)]"
              />
              <input
                data-testid={HUB.searchInput}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by address, city, or agent name..."
                className="w-full pl-12 pr-4 py-3.5 bg-white rounded-full text-sm font-medium border border-transparent shadow-[0_2px_18px_-8px_rgba(43,42,41,0.15)] focus:outline-none focus:border-[color:var(--kw-red)]/40 focus:ring-4 focus:ring-[color:var(--kw-red)]/10 transition-all"
              />
            </div>
            <button
              data-testid={HUB.postOpportunityBtn}
              onClick={() => { setPostPrefill(null); setPostOpen(true); }}
              className="kw-btn-primary inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm whitespace-nowrap"
            >
              <Plus size={18} strokeWidth={3} />
              Post Open House Opportunity
            </button>
            <button
              data-testid={CSV_MODAL.openBtn}
              onClick={() => setCsvOpen(true)}
              className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-sm whitespace-nowrap border-2 border-[color:var(--kw-charcoal)] text-[color:var(--kw-charcoal)] font-black tracking-widest uppercase hover:bg-[color:var(--kw-charcoal)] hover:text-white transition-colors"
            >
              <Upload size={16} strokeWidth={2.5} />
              Import CSV
            </button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-8">
        <div className="inline-flex items-center gap-6 border-b border-[color:var(--kw-border)] w-full">
          <button
            data-testid={HUB.tabsAvailable}
            onClick={() => setTab("available")}
            className={`relative pb-3 text-xs md:text-sm font-black tracking-widest uppercase transition-colors ${
              tab === "available"
                ? "text-[color:var(--kw-red)]"
                : "text-[color:var(--muted-foreground)] hover:text-[color:var(--kw-charcoal)]"
            }`}
          >
            <span className="inline-flex items-center gap-2">
              <HomeIcon size={14} strokeWidth={2.5} />
              Available
              <span className="text-[10px] font-bold opacity-70">({availableCount})</span>
            </span>
            {tab === "available" && (
              <span className="absolute left-0 right-0 -bottom-px h-[3px] bg-[color:var(--kw-red)] rounded-full" />
            )}
          </button>
          <button
            data-testid={HUB.tabsClaimed}
            onClick={() => setTab("claimed")}
            className={`relative pb-3 text-xs md:text-sm font-black tracking-widest uppercase transition-colors ${
              tab === "claimed"
                ? "text-[color:var(--kw-charcoal)]"
                : "text-[color:var(--muted-foreground)] hover:text-[color:var(--kw-charcoal)]"
            }`}
          >
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 size={14} strokeWidth={2.5} />
              Claimed
              <span className="text-[10px] font-bold opacity-70">({claimedCount})</span>
            </span>
            {tab === "claimed" && (
              <span className="absolute left-0 right-0 -bottom-px h-[3px] bg-[color:var(--kw-charcoal)] rounded-full" />
            )}
          </button>
        </div>
      </section>

      {/* Grid */}
      <main className="max-w-7xl mx-auto px-5 md:px-8 py-6 md:py-8">
        {visibleListings.length === 0 ? (
          <div
            data-testid={HUB.emptyState}
            className="text-center py-20 border-2 border-dashed border-[color:var(--kw-border)] rounded-2xl bg-white/60"
          >
            <div className="inline-flex w-14 h-14 rounded-full bg-[color:var(--kw-cream)] items-center justify-center mb-4">
              <HomeIcon className="text-[color:var(--muted-foreground)]" size={24} />
            </div>
            <h3 className="font-display text-2xl font-black uppercase text-[color:var(--kw-charcoal)] mb-2">
              {tab === "available" ? "No available opportunities" : "No claimed opportunities yet"}
            </h3>
            <p className="text-[color:var(--muted-foreground)] max-w-md mx-auto text-sm">
              {query
                ? "No listings match your search. Try a different address, city, or agent name."
                : tab === "available"
                ? "Be the first to post an open house opportunity for the team."
                : "Once agents start claiming listings, they'll appear here."}
            </p>
            {tab === "available" && !query && (
              <button
                onClick={() => setPostOpen(true)}
                className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm kw-btn-primary"
              >
                <Plus size={16} strokeWidth={3} />
                Post an Opportunity
              </button>
            )}
          </div>
        ) : (
          <div
            data-testid={HUB.listingsGrid}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {visibleListings.map((l, i) => (
              <ListingCard
                key={l.id}
                listing={l}
                onClaim={setClaimTarget}
                onDuplicate={(src) => {
                  setPostPrefill({
                    address: src.address,
                    city: src.city,
                    county: src.county,
                    listPrice: src.listPrice,
                    daysOnMarket: src.daysOnMarket,
                    sellerPrefTime: src.sellerPrefTime,
                    notes: src.notes,
                    agentName: src.agentName,
                    agentPhone: src.agentPhone,
                    agentEmail: src.agentEmail,
                  });
                  setPostOpen(true);
                }}
                index={i}
              />
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[color:var(--kw-border)] bg-white mt-12">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <KWLogo className="h-7" />
            <span className="text-xs text-[color:var(--muted-foreground)]">
              Open House Hub · Internal Coordination Tool
            </span>
          </div>
          <div className="text-xs text-[color:var(--muted-foreground)]">
            Each Keller Williams Realty office is independently owned and operated.
          </div>
        </div>
      </footer>

      {/* Modals */}
      <PostListingModal
        open={postOpen}
        onOpenChange={(v) => { setPostOpen(v); if (!v) setPostPrefill(null); }}
        onSubmit={handlePost}
        prefill={postPrefill}
      />
      <CsvImportModal
        open={csvOpen}
        onOpenChange={setCsvOpen}
        onImported={(docs) => setListings((prev) => [...docs, ...prev])}
      />
      <ClaimModal
        open={!!claimTarget}
        onOpenChange={(v) => !v && setClaimTarget(null)}
        listing={claimTarget}
        onConfirm={handleConfirmClaim}
      />
      <ReportActivityModal open={reportOpen} onOpenChange={setReportOpen} />
      <VideoTutorialModal
        open={!!tutorial}
        onOpenChange={(v) => !v && setTutorial(null)}
        title={tutorial ? TUTORIALS[tutorial].title : ""}
        src={tutorial ? TUTORIALS[tutorial].src : ""}
        testId={`tutorial-${tutorial || "modal"}`}
      />
    </div>
  );
};

export default OpenHouseHub;
